import { z } from "zod";
import { identifiant } from "./mesure";

/**
 * Le livre publie : l'edition telephone d'un livre, telle que la console du Lab la pousse dans
 * l'appli (`PUT /api/livre`), que l'appli la sert (`GET /api/livre`) et que le navigateur la
 * controle avant de la jouer. Spec : docs/specs/2026-10-03-console-livre-jeu-design.md (D7,
 * ticket 2 du chapeau #276).
 *
 * C'est le livre compile **sans aucun texte** (`editionTelephone` de
 * `packages/livre-jeu/src/compiler.ts`). Le contrat l'exige au lieu de le supposer : le texte
 * d'une scene et la consigne papier d'une enigme valent `""`, une scene n'a pas de titre, et
 * tout champ de plus est refuse. C'est la garantie qu'aucun texte du livre n'entre dans l'appli,
 * ni par la console, ni par la base, ni par la copie gardee dans un navigateur.
 *
 * Restent les libelles des choix (imprimes a cote des QR codes), la question et les solutions
 * d'une enigme (le telephone les pose et les compare), le titre du livre et les libelles des
 * indices et des jauges (le carnet les montre, comme la feuille d'aventure). Leurs bornes de
 * longueur tiennent dehors un texte de scene qui s'y glisserait.
 *
 * Au-dela de la forme, des controles de coherence legers (`incoherences`) : ce sans quoi le
 * moteur du telephone casserait ou perdrait la partie (une suite vers une scene inconnue, un
 * indice que le carnet ne connait pas, deux choix sous le meme code). Le sens du livre (fins
 * atteignables, conditions verifiables sur papier) reste l'affaire du verificateur du moteur,
 * que la console appelle avant de publier.
 */

/**
 * Les limites du livre : celles du code de sauvegarde (`LIMITES` de `moteur/sauvegarde.ts`),
 * au-dela desquelles le telephone ne saurait plus l'ecrire. `livre.test.ts` du frontend verifie
 * qu'elles restent les memes.
 */
export const LIMITES_DU_LIVRE = { scenes: 256, indices: 12, jauges: 3, maxJauge: 15 } as const;

/** Le code d'un choix, comme l'ecrit le compilateur : une lettre puis deux caracteres de la base 32 de Crockford. */
const code = z
  .string()
  .regex(
    /^[A-HJKMNP-TV-Z][0-9A-HJKMNP-TV-Z]{2}$/,
    "code hors format : une lettre puis deux caracteres de la base 32 de Crockford",
  );

/** Un texte court du livre (libelle, question, titre), jamais vide. */
const court = (max: number) => z.string().max(max).regex(/\S/, "un texte non vide est attendu");

const page = z.int().min(1).max(LIMITES_DU_LIVRE.scenes);
const borneDeJauge = z.int().min(0).max(LIMITES_DU_LIVRE.maxJauge);

const atome = z.union([
  z.strictObject({ a: identifiant }),
  z.strictObject({ pas: identifiant }),
  z
    .strictObject({
      jauge: identifiant,
      min: borneDeJauge.exactOptional(),
      max: borneDeJauge.exactOptional(),
    })
    .refine(
      (condition) => condition.min !== undefined || condition.max !== undefined,
      "une condition de jauge demande min, max ou les deux",
    ),
]);

const condition = z.union([
  atome,
  z.strictObject({ tous: z.array(atome).min(2).max(LIMITES_DU_LIVRE.indices) }),
  z.strictObject({ un: z.array(atome).min(2).max(LIMITES_DU_LIVRE.indices) }),
]);

const indicesTouches = z.array(identifiant).max(LIMITES_DU_LIVRE.indices);
const effets = {
  gagne: indicesTouches.exactOptional(),
  perd: indicesTouches.exactOptional(),
  jauges: z
    .record(
      identifiant,
      z.int().refine((ecart) => ecart !== 0, "un ecart entier non nul est attendu"),
    )
    .exactOptional(),
};

/*
 * Les cles de chaque objet sont declarees dans l'ordre ou le compilateur les ecrit : le livre
 * relu par ce schema se serialise alors octet pour octet comme l'edition sortie du compilateur,
 * meme relu d'un jsonb qui ne garde pas l'ordre des cles. L'empreinte de la console se
 * recalcule donc sur le livre que sert l'appli.
 */

/** Une suite d'enigme (juste ou faux) n'a pas de condition : la reponse decide. */
const suiteDEnigme = z.strictObject({ va: identifiant, ...effets });
const suite = z.strictObject({
  va: identifiant,
  si: condition.exactOptional(),
  gagne: effets.gagne,
  perd: effets.perd,
  jauges: effets.jauges,
});

const enigme = z.strictObject({
  // Une reponse se tape au telephone : courte, et quelques graphies admises au plus.
  solutions: z.array(z.string().min(1).max(60)).min(1).max(10),
  papier: z.literal(
    "",
    "la consigne papier d'une enigme reste vide : elle s'imprime, elle n'entre pas dans l'appli",
  ),
  juste: suiteDEnigme,
  faux: suiteDEnigme,
  question: court(240).exactOptional(),
});

const choix = z
  .strictObject({
    code,
    id: identifiant,
    libelle: court(240),
    suites: z.array(suite).min(1).max(20).exactOptional(),
    enigme: enigme.exactOptional(),
  })
  .refine(
    (un) => (un.suites === undefined) !== (un.enigme === undefined),
    "un choix porte des suites ou une enigme, l'un ou l'autre",
  );

const scene = z.strictObject({
  id: identifiant,
  page,
  texte: z.literal(
    "",
    "le texte d'une scene reste vide : il se lit sur papier, il n'entre pas dans l'appli",
  ),
  fin: z.enum(["reussie", "ratee"]).exactOptional(),
  choix: z.array(choix).max(20),
});

const indice = z.strictObject({ id: identifiant, libelle: court(120) });

const jauge = z
  .strictObject({
    id: identifiant,
    libelle: court(120),
    max: z.int().min(1).max(LIMITES_DU_LIVRE.maxJauge),
    depart: borneDeJauge,
  })
  .refine((une) => une.depart <= une.max, "le depart d'une jauge ne depasse pas son max");

/** La forme seule, sans la coherence : ce que lit `incoherences`. */
const formeDuLivre = z.strictObject({
  format: z.literal(1),
  id: identifiant,
  titre: court(160),
  domaine: z
    .string()
    .max(253)
    .regex(/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/, "un nom de domaine est attendu"),
  depart: identifiant,
  indices: z.array(indice).max(LIMITES_DU_LIVRE.indices),
  jauges: z.array(jauge).max(LIMITES_DU_LIVRE.jauges),
  scenes: z.array(scene).min(1).max(LIMITES_DU_LIVRE.scenes),
});

type FormeDuLivre = z.infer<typeof formeDuLivre>;
type Condition = z.infer<typeof condition>;
type Atome = z.infer<typeof atome>;
type Effets = z.infer<typeof suiteDEnigme>;

/** Une faute de coherence, avec le chemin de ce qui la porte dans le livre. */
export interface Incoherence {
  chemin: (string | number)[];
  message: string;
}

/**
 * Les controles de coherence legers d'un livre deja bien forme. Rien ici ne parcourt les etats
 * d'une partie : seulement les references (scenes, indices, jauges) et l'unicite de ce que le
 * telephone cherche par sa cle (scene, code, page).
 */
export function incoherences(livre: FormeDuLivre): Incoherence[] {
  const fautes: Incoherence[] = [];
  const faute = (chemin: (string | number)[], message: string) => {
    fautes.push({ chemin, message });
  };

  const unique = <T>(liste: T[], quoi: string, cle: (element: T) => string | number) => {
    const vus = new Set<string | number>();
    liste.forEach((element, rang) => {
      const valeur = cle(element);
      if (vus.has(valeur)) faute([quoi, rang], `${quoi} en double : ${valeur}`);
      vus.add(valeur);
    });
  };
  unique(livre.scenes, "scenes", (une) => une.id);
  unique(livre.indices, "indices", (un) => un.id);
  unique(livre.jauges, "jauges", (une) => une.id);

  const scenes = new Set(livre.scenes.map((une) => une.id));
  const indices = new Set(livre.indices.map((un) => un.id));
  const jauges = new Map(livre.jauges.map((une) => [une.id, une]));

  const depart = livre.scenes.find((une) => une.id === livre.depart);
  if (!depart) faute(["depart"], `scene de depart inconnue : ${livre.depart}`);
  else if (depart.page !== 1) faute(["depart"], "la scene de depart est en page 1");

  // Les pages d'un livre compile vont de 1 au nombre de scenes, une scene par page.
  const pages = new Set<number>();
  livre.scenes.forEach((une, rang) => {
    if (une.page > livre.scenes.length) {
      faute(
        ["scenes", rang, "page"],
        `page ${une.page} hors du livre (1 a ${livre.scenes.length})`,
      );
    }
    if (pages.has(une.page)) faute(["scenes", rang, "page"], `page ${une.page} en double`);
    pages.add(une.page);
  });

  const atomeConnu = (un: Atome, chemin: (string | number)[]) => {
    if ("a" in un && !indices.has(un.a)) faute(chemin, `indice inconnu : ${un.a}`);
    if ("pas" in un && !indices.has(un.pas)) faute(chemin, `indice inconnu : ${un.pas}`);
    if ("jauge" in un) {
      const connue = jauges.get(un.jauge);
      if (!connue) faute(chemin, `jauge inconnue : ${un.jauge}`);
      else if ([un.min, un.max].some((borne) => borne !== undefined && borne > connue.max)) {
        faute(chemin, `borne hors de la jauge ${connue.id} (0 a ${connue.max})`);
      }
    }
  };
  const conditionConnue = (si: Condition, chemin: (string | number)[]) => {
    if (!("tous" in si) && !("un" in si)) {
      atomeConnu(si, chemin);
      return;
    }
    const [quoi, atomes] = "tous" in si ? (["tous", si.tous] as const) : (["un", si.un] as const);
    for (const [rang, un] of atomes.entries()) atomeConnu(un, [...chemin, quoi, rang]);
  };
  const suiteConnue = (une: Effets & { si?: Condition }, chemin: (string | number)[]) => {
    if (!scenes.has(une.va)) faute([...chemin, "va"], `va vers une scene inconnue : ${une.va}`);
    for (const cle of ["gagne", "perd"] as const) {
      for (const id of une[cle] ?? []) {
        if (!indices.has(id)) faute([...chemin, cle], `indice inconnu : ${id}`);
      }
    }
    for (const nom of Object.keys(une.jauges ?? {})) {
      if (!jauges.has(nom)) faute([...chemin, "jauges"], `jauge inconnue : ${nom}`);
    }
    if (une.si) conditionConnue(une.si, [...chemin, "si"]);
  };

  const codes = new Set<string>();
  livre.scenes.forEach((une, rangScene) => {
    const ici = ["scenes", rangScene] as const;
    const ids = new Set<string>();
    une.choix.forEach((un, rangChoix) => {
      const chemin = [...ici, "choix", rangChoix];
      if (codes.has(un.code)) faute([...chemin, "code"], `code en double : ${un.code}`);
      codes.add(un.code);
      if (ids.has(un.id)) faute([...chemin, "id"], `choix en double dans la scene : ${un.id}`);
      ids.add(un.id);
      for (const [rang, suivante] of (un.suites ?? []).entries()) {
        suiteConnue(suivante, [...chemin, "suites", rang]);
      }
      if (un.enigme) {
        suiteConnue(un.enigme.juste, [...chemin, "enigme", "juste"]);
        suiteConnue(un.enigme.faux, [...chemin, "enigme", "faux"]);
      }
    });
  });
  return fautes;
}

/**
 * L'edition telephone d'un livre : la forme stricte, puis la coherence. Le meme schema sert la
 * publication (backend), le livre servi (backend) et le livre charge (navigateur).
 */
export const editionTelephoneSchema = formeDuLivre.superRefine((livre, contexte) => {
  for (const { chemin, message } of incoherences(livre)) {
    contexte.addIssue({ code: "custom", path: chemin, message });
  }
});
export type EditionTelephone = z.infer<typeof editionTelephoneSchema>;

/** L'empreinte d'une edition, calculee par la console : SHA-256 en hexadecimal minuscule. */
export const empreinteSchema = z
  .string()
  .regex(/^[0-9a-f]{64}$/, "empreinte attendue : SHA-256 en hexadecimal minuscule, 64 caracteres");

/** Une publication : ce que la console envoie, ce que l'appli garde et sert. */
export const publicationSchema = z.strictObject({
  livre: editionTelephoneSchema,
  empreinte: empreinteSchema,
  publie_le: z.iso.datetime({ offset: true }),
});
export type Publication = z.infer<typeof publicationSchema>;
